/// <reference types="vite/client" />
import "@delaget/react-datez/dist/css/react-datez.css";
import {Provider} from "react-redux";
import * as Sentry from "@sentry/react";
import App from "./App";
import {getHistory, updateHistory} from "@/appHistory";
import "semantic-ui-less/semantic.less"; // this needs to be loaded before our own style
import "@/scss/style.scss";
import store from "@/state/store";
import {createRoot} from "react-dom/client";
import {ENV, setLibraryMode, updateRuntimeConfig} from "@/configuration";

// Configure Sentry error tracking
// To support environment transformation with Octopus, the config file must be outside of the
// compiled files. config.json is in the public folder which will not be compiled and will be
// transformed by Octopus for the correct environment.

if (ENV.LIBRARY_MODE) {
    // In library mode, initialize Sentry with default values
    Sentry.init({
        dsn: "https://0a9349320be54f0798eeef48e950981d@sentry.io/1867546",
        environment: "Production",
        release: "delaget@1.0.0",
        tracesSampleRate: 0
    });
} else {
    // When running standalone, fetch configuration from config.json to setup Sentry
    fetch("/config.json")
        .then((response) => {
            return response.json();
        })
        .then((parsedConfig) => {
            // In production do not sample front end performance, in lower environments, trace them all.
            const tracesSampleRate =
                parsedConfig["environment"] === "Production" ? 0 : 1.0;

            Sentry.init({
                dsn: "https://0a9349320be54f0798eeef48e950981d@sentry.io/1867546",
                environment: parsedConfig["environment"],
                release: "delaget@" + parsedConfig["release"],
                tracesSampleRate: tracesSampleRate
            });
        })
        .catch(() => {
            Sentry.init({
                dsn: "https://0a9349320be54f0798eeef48e950981d@sentry.io/1867546",
                tracesSampleRate: 0
            });
        });
}

// Define the React app wrapped with the Provider
const Root = ({history}: {history: any}) => (
    <Provider store={store}>
        <App history={history} />
    </Provider>
);

/**
 * Mount the dashboard to a DOM element
 * @param element - The DOM element to mount the dashboard to
 * @param environment - The environment for the Delaget API calls when in library mode. ("dev", "test", "stage", "prod")
 * @param routerBasePath - The base href for the router when in library mode. Also used as DataCentral API Domain. ("portal1234" for https://dev.parops.com/portal1234/insights. DC API calls same-site at "/portal1234/api/...")
 * @param appBasePath - The base URL for the Delaget app when in library mode. ("insights" for https://dev.parops.com/portal1234/insights)
 * @returns A function to unmount the dashboard
 */
export function mount(
    element: HTMLElement,
    environment?: string,
    routerBasePath?: string,
    appBasePath?: string
) {
    // Update runtime configuration if provided
    updateRuntimeConfig(environment, routerBasePath, appBasePath);

    // Recreate history with new basename
    updateHistory();

    const history = getHistory();
    const root = createRoot(element);
    root.render(<Root history={history} />);
    return () => root.unmount();
}

// todo: Keeping separate for now since mf_mount needs to set library mode to true, and to avoid breaking existing functionality
// while we transition to module federation.
export function mf_mount(
    element: HTMLElement,
    environment?: string,
    routerBasePath?: string,
    appBasePath?: string
) {
    setLibraryMode(true);
    mount(element, environment, routerBasePath, appBasePath);
}

// Auto-initialize if we're in a browser environment and not being imported as a module
if (typeof window !== "undefined" && !ENV.LIBRARY_MODE) {
    const rootElement = document.getElementById("root");
    if (rootElement) {
        mount(rootElement);
    } else {
        console.error("Root element not found");
    }
}
